import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, Button, Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, FiberManualRecord } from '@mui/icons-material';

// Main App component which renders the ImageCarousel
function App(products) {
  const [activeStep, setActiveStep] = useState(0); // State to manage the currently active slide index
  const autoPlayIntervalRef = useRef(null); // Ref to hold the interval ID for autoplay
  const isHovered = useRef(false); // Ref to track if the carousel is hovered
  const [generatedDescription, setGeneratedDescription] = useState(''); // State for LLM-generated description
  const [isLoading, setIsLoading] = useState(false); // State for loading indicator

  // State for swipe functionality
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const carouselRef = useRef(null); // Ref to the carousel container for event listeners

  // Define an array of products for the carousel, each with a name, description, and image URL.

  const totalSlides = products.length; // Total number of slides

  // Function to navigate to the next slide, wrapped in useCallback for dependencies
  const handleNext = useCallback(() => {
    setActiveStep((prevIndex) => (prevIndex + 1) % totalSlides);
    setGeneratedDescription(''); // Clear generated description on slide change
  }, [totalSlides]);

  // Function to navigate to the previous slide, wrapped in useCallback for dependencies
  const handleBack = useCallback(() => {
    setActiveStep((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
    setGeneratedDescription(''); // Clear generated description on slide change
  }, [totalSlides]);

  // Function to directly jump to a slide
  const handleDotClick = (index) => {
    setActiveStep(index);
    setGeneratedDescription(''); // Clear generated description on slide change
  };

  // Effect for autoplay functionality
  useEffect(() => {
    const startAutoPlay = () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
      autoPlayIntervalRef.current = setInterval(() => {
        if (!isHovered.current && !isLoading) {
          handleNext();
        }
      }, 5000); // Autoplay interval: 5 seconds
    };

    startAutoPlay();

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [totalSlides, handleNext, isLoading]);

  // Event handlers for mouse enter and leave to control autoplay on hover
  const handleMouseEnter = () => {
    isHovered.current = true;
  };

  const handleMouseLeave = () => {
    isHovered.current = false;
  };

  // ✨ Gemini API Integration: Function to generate description
  const generateNewDescription = async () => {
    setIsLoading(true);
    const currentItemName = products[activeStep].name;
    const prompt = `Generate a concise and creative description (around 20-30 words) for an image that has the title: '${currentItemName}'. Make it engaging and suitable for a carousel.`;

    let chatHistory = [];
    chatHistory.push({ role: "user", parts: [{ text: prompt }] });
    const payload = { contents: chatHistory };
    const apiKey = ""; // Canvas will automatically provide this at runtime
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

    try {
      let response;
      let delay = 1000;
      for (let i = 0; i < 3; i++) {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const text = result.candidates[0].content.parts[0].text;
        setGeneratedDescription(text);
      } else {
        console.error("Gemini API returned an unexpected structure or no content.");
        setGeneratedDescription("Could not generate a new description. Please try again.");
      }
    } catch (error) {
      console.error("Error calling Gemini API:", error);
      setGeneratedDescription("Failed to generate description due to an error.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Swipe Functionality ---
  const handleTouchStart = (e) => {
    // Check if the target is the image, and prevent default if it is to avoid native image drag
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
    }
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchEndX(null); // Reset EndX on new touch start
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return; // No valid swipe

    const minSwipeDistance = 50; // Minimum distance for a swipe to be recognized
    const distance = touchStartX - touchEndX;

    if (distance > minSwipeDistance) {
      // Swiped left
      handleNext();
    } else if (distance < -minSwipeDistance) {
      // Swiped right
      handleBack();
    }

    // Reset touch coordinates
    setTouchStartX(null);
    setTouchEndX(null);
  };

  // Mouse drag functionality for desktop
  const [mouseDownX, setMouseDownX] = useState(null);
  const [mouseIsDown, setMouseIsDown] = useState(false);

  const handleMouseDown = (e) => {
    // Check if the target is the image, and prevent default if it is to avoid native image drag
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
    }
    setMouseDownX(e.clientX);
    setMouseIsDown(true);
  };

  const handleMouseMove = (e) => {
    if (!mouseIsDown) return;
    // Prevent default to avoid image dragging on desktop
    e.preventDefault();
    setTouchEndX(e.clientX); // Using touchEndX to store current mouse position
  };

  const handleMouseUp = () => {
    if (!mouseIsDown) return;

    setMouseIsDown(false);
    if (mouseDownX === null || touchEndX === null) return;

    const minDragDistance = 50;
    const distance = mouseDownX - touchEndX;

    if (distance > minDragDistance) {
      handleNext();
    } else if (distance < -minDragDistance) {
      handleBack();
    }

    setMouseDownX(null);
    setTouchEndX(null);
  };

  // Add event listeners for mouse drag on component mount
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (carouselElement) {
      carouselElement.addEventListener('mousedown', handleMouseDown);
      carouselElement.addEventListener('mousemove', handleMouseMove);
      carouselElement.addEventListener('mouseup', handleMouseUp);
      carouselElement.addEventListener('mouseleave', handleMouseUp); // End drag if mouse leaves element

      return () => {
        carouselElement.removeEventListener('mousedown', handleMouseDown);
        carouselElement.removeEventListener('mousemove', handleMouseMove);
        carouselElement.removeEventListener('mouseup', handleMouseUp);
        carouselElement.removeEventListener('mouseleave', handleMouseUp);
      };
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);


  return (
    <div className="min-h-screen flex products-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4 font-sans antialiased">
      <div
        ref={carouselRef} // Attach ref for event listeners
        className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
        onMouseEnter={handleMouseEnter} // Pause autoplay on mouse enter
        onMouseLeave={handleMouseLeave} // Resume autoplay on mouse leave
        onTouchStart={handleTouchStart} // Touch swipe start
        onTouchMove={handleTouchMove}   // Touch swipe move
        onTouchEnd={handleTouchEnd}     // Touch swipe end
      >
        {/* Carousel Content Area */}
        <Paper
          className="p-6 text-center rounded-t-xl flex flex-col justify-between products-center min-h-[500px] sm:min-h-[450px] bg-white transition-all duration-300"
          style={{ background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2f7 100%)' }}
        >
          <img
            src={products[activeStep].image}
            alt={products[activeStep].name}
            className="max-w-full h-auto rounded-lg mb-4 object-cover border border-gray-200 shadow-sm transition-transform duration-300 hover:scale-[1.01] pointer-events-none" /* Added pointer-events-none here */
            style={{ maxHeight: '350px', width: '100%' }}
            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/800x400/DC3545/FFFFFF?text=Image+Load+Error"; }}
          />
          {/* Explicitly apply text-center to Typography components */}
          <Typography variant="h5" component="h2" className="text-3xl sm:text-2xl font-extrabold text-gray-800 mb-2 font-inter text-center">
            {products[activeStep].name}
          </Typography>
          <Box className="min-h-[60px] flex products-center justify-center"> {/* Added fixed height for description area */}
            {isLoading ? (
              <CircularProgress size={30} color="primary" /> // Show loading spinner
            ) : (
              <Typography variant="body1" className="text-lg sm:text-md text-gray-600 mb-4 font-inter text-center">
                {generatedDescription || products[activeStep].description} {/* Show generated or original description */}
              </Typography>
            )}
          </Box>
          <div className="flex flex-col sm:flex-row gap-4 mt-2">
            <Button
              variant="contained"
              className="text-white font-semibold py-3 px-8 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
              style={{
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 10px 2px rgba(33, 203, 243, .4)'
              }}
            >
              Explore More
            </Button>
            <Button
              variant="outlined"
              onClick={generateNewDescription}
              disabled={isLoading} // Disable button while loading
              className="text-blue-600 border-blue-600 font-semibold py-3 px-8 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 hover:bg-blue-50 hover:shadow-xl"
              style={{
                borderColor: '#2196F3',
                color: '#2196F3',
                boxShadow: '0 3px 10px 2px rgba(33, 203, 243, .1)'
              }}
            >
              ✨ Generate New Description
            </Button>
          </div>
        </Paper>

        {/* Navigation Buttons (Left/Right) - Enhanced placement and styling */}
        <IconButton
          className="absolute top-1/2 left-8 transform -translate-y-1/2 bg-blue-700 text-white p-4 rounded-full shadow-xl hover:bg-blue-800 transition-all duration-300 z-20 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50"
          onClick={handleBack}
          aria-label="Previous slide"
        >
          <KeyboardArrowLeft fontSize="large" />
        </IconButton>
        <IconButton
          className="absolute top-1/2 right-8 transform -translate-y-1/2 bg-blue-700 text-white p-4 rounded-full shadow-xl hover:bg-blue-800 transition-all duration-300 z-20 focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50"
          onClick={handleNext}
          aria-label="Next slide"
        >
          <KeyboardArrowRight fontSize="large" />
        </IconButton>

        {/* Custom Clickable Dot Indicators */}
        <Box className="flex justify-center mt-4 pb-4 bg-white rounded-b-xl">
          {products.map((_, idx) => (
            <IconButton
              key={idx}
              onClick={() => handleDotClick(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`mx-1 p-1 transition-all duration-300 ${
                idx === activeStep ? 'text-blue-600 scale-125' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <FiberManualRecord fontSize="small" />
            </IconButton>
          ))}
        </Box>
      </div>
    </div>
  );
}

export default App;
